import reactUpdate from 'immutability-helper';
import React from 'react';

import ChooseProteinAlignmentPane from 'analyses/protein-alignment/ChooseProteinAlignmentPane';
import ConfirmProteinAlignmentPane from 'analyses/protein-alignment/ConfirmProteinAlignmentPane';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import ProteinAlignmentActions from 'molbio/proteins/alignments/ProteinAlignmentActions';
import notifications from 'notifications';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import * as ModalContainer from 'ui-library/ModalContainer';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import {Optional} from 'util/Types';

import {ProteinSpecifier} from './util/ProteinAlignmentConstants';

const PROTEIN_ALIGNMENT_MODAL_KEY = 'ProteinAlignmentModal';

const CHOOSE_PANE = 'CHOOSE_PANE'; // First step
const CONFIRM_PANE = 'CONFIRM_PANE';

interface Props {
  initialProteinSpecifiers: Array<ProteinSpecifier>;
  initialTemplateProteinIndex?: number;
}

interface State {
  proteinSpecifiers: Array<ProteinSpecifier>;
  templateProteinIndex: Optional<number>;
  proteinName: string | null;
  folderId: Optional<string>;
  currentPane: string;
}

class ProteinAlignmentModal extends React.Component<Props, State> {
  static open({initialProteinSpecifiers}: Props): void {
    ModalContainer.open(
      PROTEIN_ALIGNMENT_MODAL_KEY,
      <ProteinAlignmentModal initialProteinSpecifiers={initialProteinSpecifiers} />
    );
  }

  static openWithTemplateSpecifier(templateProteinSpecifier: ProteinSpecifier): void {
    ModalContainer.open(
      PROTEIN_ALIGNMENT_MODAL_KEY,
      <ProteinAlignmentModal
        initialTemplateProteinIndex={0}
        initialProteinSpecifiers={[templateProteinSpecifier]}
      />
    );
  }

  state: State = {
    proteinSpecifiers: this.props.initialProteinSpecifiers,
    templateProteinIndex: this.props.initialTemplateProteinIndex,
    proteinName: null,
    folderId: FolderDefaults.get(FolderDefaultsKey.INVENTORY),
    currentPane: CHOOSE_PANE,
  };

  _shouldCreateNewProtein = (): boolean => this.state.templateProteinIndex == null;

  _shouldEnableCreateAlignment = (): boolean => {
    if (this._shouldCreateNewProtein()) {
      return this.state.proteinName != null && this.state.folderId != null;
    } else {
      return true;
    }
  };

  _handleRequestClose = (): void => {
    ModalContainer.close(PROTEIN_ALIGNMENT_MODAL_KEY);
  };

  _handleAddProteinSpecifier = (proteinSpecifier: ProteinSpecifier): void => {
    this.setState({
      proteinSpecifiers: reactUpdate(this.state.proteinSpecifiers, {$push: [proteinSpecifier]}),
    });
  };

  _handleRemoveProteinAt = (index: number): void => {
    this.setState({
      proteinSpecifiers: reactUpdate(this.state.proteinSpecifiers, {$splice: [[index, 1]]}),
    });
    if (index < this.state.templateProteinIndex!) {
      this.setState({templateProteinIndex: this.state.templateProteinIndex! - 1});
    } else if (index === this.state.templateProteinIndex) {
      this.setState({templateProteinIndex: null});
    }
  };

  _handleSetTemplateProteinAt = (maybeIndex: number | null): void => {
    if (maybeIndex != null && !this.state.proteinSpecifiers[maybeIndex].canBeTemplate) {
      notifications.warn('Cannot set read-only AA sequence as template.', {
        id: 'ProteinAlignmentModal: Cannot set read-only AA sequence as template.',
      });
    } else {
      this.setState({
        templateProteinIndex: maybeIndex,
      });
    }
  };

  _handleSetProteinName = (proteinName: string): void => {
    this.setState({proteinName});
  };

  _handleSetFolderId = (folderId: string): void => {
    this.setState({folderId});
    FolderDefaults.set(FolderDefaultsKey.INVENTORY, folderId);
  };

  _handleCreateAlignment = (): void => {
    ProteinAlignmentActions.create({
      proteinName: this.state.proteinName!,
      folderId: this._shouldCreateNewProtein() ? this.state.folderId! : undefined,
      templateProteinIndex: this.state.templateProteinIndex,
      proteinSpecifiers: this.state.proteinSpecifiers,
    });
    this._handleRequestClose();
  };

  _getButtons: () => Array<ModalButton> = () => {
    if (this.state.currentPane === CHOOSE_PANE) {
      return [
        {
          text: 'Next',
          style: 'primary',
          disabled: this.state.proteinSpecifiers.length <= 1,
          onClick: () => {
            this.setState({currentPane: CONFIRM_PANE});
          },
          key: 'next',
          dataTestAttrs: {
            component: 'ProteinAlignmentModal',
            element: 'footerButton',
            key: ModalButtonKey.NEXT,
          },
        },
      ];
    } else {
      return [
        {
          text: 'Back',
          pullLeft: true,
          onClick: () => {
            this.setState({currentPane: CHOOSE_PANE});
          },
          key: 'back',
        },
        {
          text: 'Create Alignment',
          style: 'primary',
          disabled: !this._shouldEnableCreateAlignment(),
          onClick: this._handleCreateAlignment,
          key: 'create',
          dataTestAttrs: {
            component: 'ProteinAlignmentModal',
            element: 'footerButton',
            key: ModalButtonKey.CREATE,
          },
        },
      ];
    }
  };

  _renderPane = (): JSX.Element => {
    if (this.state.currentPane === CHOOSE_PANE) {
      return (
        <ChooseProteinAlignmentPane
          proteinSpecifiers={this.state.proteinSpecifiers}
          templateProteinIndex={this.state.templateProteinIndex}
          onAddProteinSpecifier={this._handleAddProteinSpecifier}
          onRemoveProteinAt={this._handleRemoveProteinAt}
          onSetTemplateProteinAt={this._handleSetTemplateProteinAt}
        />
      );
    } else {
      return (
        <ConfirmProteinAlignmentPane
          shouldCreateNewProtein={this._shouldCreateNewProtein()}
          proteinName={this.state.proteinName}
          onSetProteinName={this._handleSetProteinName}
          folderId={this.state.folderId}
          onSetFolderId={this._handleSetFolderId}
        />
      );
    }
  };

  render(): JSX.Element {
    return (
      <Modal
        title="Create protein alignment"
        onRequestClose={this._handleRequestClose}
        buttons={this._getButtons()}
      >
        {this._renderPane()}
      </Modal>
    );
  }
}

export default ProteinAlignmentModal;
